import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { NavController } from '@ionic/angular';
import { SlickCarouselComponent } from 'ngx-slick-carousel';
import { AppService } from 'src/app/_services/app.service';
import { LocalStorageService } from 'src/app/_services/localStorage.service';

declare var $: any; // jQuery
declare var slick: any; // Slick carousel

@Component({
  selector: 'app-splash-screen',
  templateUrl: './splash.component.html',
  styleUrls: ['./splash.component.scss'],
})
export class SplashComponent implements OnInit, AfterViewInit {
  @ViewChild('slickModal') slickModal!: SlickCarouselComponent;
  lastSlide = false
  // Sur iOS/Android, si jQuery/Slick n'est pas prêt, ngx-slick-carousel peut casser le rendu => écran blanc.
  // On n'affiche le carousel que si Slick est réellement disponible.
  slickAvailable = false;
  slideConfig: any = {
    "slidesToShow": 1,
    "slidesToScroll": 1,
    "dots": true,
    "infinite": false,
    "swipe": true,
    "speed": 500,
    "arrows": false
  };
  index = 0;
  loaded = false;
  role: any;
  init = false;
  constructor(private appService: AppService, private navCtrl: NavController, private cdf: ChangeDetectorRef,
    private localStorageService: LocalStorageService) { }

  async ngOnInit() {
    this.role = this.localStorageService.getUserRole();
    this.appService.setSpashViewed(1);

    try {
      // Attendre que les styles soient appliqués avant d'afficher le contenu
      await this.waitForStylesReady();

      // Attendre que jQuery et Slick soient disponibles
      await this.waitForSlickLibrary();

      // Déterminer si Slick est vraiment disponible (sinon on fallback sans carousel)
      this.slickAvailable =
        typeof $ !== 'undefined' &&
        typeof $.fn !== 'undefined' &&
        typeof $.fn.slick !== 'undefined';
      this.cdf.detectChanges();

      // Précharger les images avant d'afficher
      await this.preloadImages();

      // Délai minimum pour éviter le flash (attendre que tout soit stable)
      await new Promise<void>((resolve) => setTimeout(resolve, 500));
    } finally {
      // Fallback de sécurité: ne jamais rester bloqué derrière l'overlay blanc.
      this.loaded = true;
      this.cdf.detectChanges();
    }
  }

  private async preloadImages(): Promise<void> {
    const imagePaths = [
      '../../../assets/splash/splash-img-1.svg',
      '../../../assets/splash/splash-img-2.svg',
      '../../../assets/splash/splash-img-3.svg',
      '../../../assets/splash/splash-img-4.svg'
    ];

    const imagePromises = imagePaths.map((path) => {
      return new Promise<void>((resolve) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => resolve(); // Continuer même si une image échoue
        img.src = path;
      });
    });

    // Attendre que toutes les images soient chargées avec timeout
    await Promise.race([
      Promise.all(imagePromises),
      new Promise<void>((resolve) => setTimeout(resolve, 3000)), // Timeout de 3 secondes
    ]);
  }

  private async waitForStylesReady(): Promise<void> {
    return new Promise<void>((resolve) => {
      const maxWait = 4000; // 4 secondes maximum
      const startTime = Date.now();

      const checkStyles = () => {
        const elapsed = Date.now() - startTime;

        // Vérifier que les styles sont appliqués sur body
        const bodyStyles = window.getComputedStyle(document.body);
        const hasStyles = bodyStyles.fontFamily && bodyStyles.fontSize;

        // Vérifier spécifiquement que les styles slick-carousel sont chargés
        const testElement = document.createElement('div');
        testElement.className = 'slick-slider';
        document.body.appendChild(testElement);
        const slickStyles = window.getComputedStyle(testElement);
        const hasSlickStyles = slickStyles.display !== undefined;
        document.body.removeChild(testElement);

        // Vérifier aussi que les feuilles de style sont chargées
        const stylesheetsReady = Array.from(document.styleSheets).every((sheet) => {
          try {
            return sheet.cssRules && sheet.cssRules.length > 0;
          } catch {
            // Cross-origin stylesheet, considérer comme OK après 2s
            return elapsed > 2000;
          }
        });

        if ((hasStyles && hasSlickStyles && stylesheetsReady) || elapsed > maxWait) {
          resolve();
        } else {
          setTimeout(checkStyles, 50);
        }
      };

      // Commencer après un petit délai pour laisser Angular initialiser
      setTimeout(checkStyles, 200);
    });
  }

  private async waitForSlickLibrary(): Promise<void> {
    return new Promise<void>((resolve) => {
      const maxWait = 5000; // 5 secondes maximum
      const startTime = Date.now();

      const checkLibrary = () => {
        const elapsed = Date.now() - startTime;
        
        // Vérifier que jQuery et Slick sont disponibles
        const jqueryReady = typeof $ !== 'undefined' && typeof $.fn !== 'undefined';
        const slickReady = typeof $.fn !== 'undefined' && typeof $.fn.slick !== 'undefined';

        if ((jqueryReady && slickReady) || elapsed > maxWait) {
          resolve();
        } else {
          setTimeout(checkLibrary, 100);
        }
      };

      checkLibrary();
    });
  }

  onAfterChange(event: any) {
    this.index = event.currentSlide
  }
  goNext() {
    if (this.index == 3) {
      this.finish()
    }
    else {
      if (this.slickAvailable) {
        this.slickModal?.slickNext();
      } else {
        // Fallback sans carousel: avancer manuellement
        this.index = Math.min(3, this.index + 1);
      }
    }
  }
  finish() {
    let baseUrl = "";
    switch (this.role) {
      case "client":
        baseUrl = "particulier";
        break;
      case "prestataire":
        baseUrl = "prestataire";
        break;
      default:
        baseUrl = "particulier";
        break;
    }
    this.navCtrl.navigateForward(["/" + baseUrl], { replaceUrl: true });
  }

  async ngAfterViewInit() {
    // Attendre que le ViewChild soit disponible
    await this.waitForViewChild();
    
    // Initialiser le carousel avec écoute des événements Slick
    await this.initializeCarouselWithEvents();
  }

  private async waitForViewChild(): Promise<void> {
    return new Promise<void>((resolve) => {
      const maxWait = 3000; // 3 secondes maximum
      const startTime = Date.now();

      const checkViewChild = () => {
        const elapsed = Date.now() - startTime;
        
        if (this.slickModal || elapsed > maxWait) {
          resolve();
        } else {
          setTimeout(checkViewChild, 50);
        }
      };

      checkViewChild();
    });
  }

  private async initializeCarouselWithEvents(): Promise<void> {
    if (this.init) {
      return;
    }

    if (!this.slickModal) {
      // Si le ViewChild n'est toujours pas disponible, attendre un peu plus
      await new Promise<void>((resolve) => setTimeout(resolve, 500));
      if (!this.slickModal) {
        // Fallback : afficher quand même sans carousel
        this.loaded = true;
        this.cdf.detectChanges();
        return;
      }
    }

    try {
      this.init = true;
      
      // Attendre que le DOM soit complètement rendu
      await new Promise<void>((resolve) => setTimeout(resolve, 200));
      
      // Trouver l'élément slick dans le DOM
      const carouselElement = document.querySelector('.splash-screen__carousel .slick-slider') as HTMLElement;
      
      if (!carouselElement && typeof $ !== 'undefined') {
        // Si le carousel n'est pas encore créé, attendre qu'il soit initialisé par ngx-slick-carousel
        await this.waitForSlickElement();
      }

      // Utiliser les événements Slick pour savoir quand le carousel est vraiment prêt
      await this.waitForSlickReady();
      
      // S'assurer que le carousel est à la première slide
      if (this.slickModal) {
        this.slickModal.slickGoTo(0);
      }
      
      // Attendre un frame de rendu pour que les styles soient appliqués
      await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
      
      // Attendre que le carousel soit visible et rendu correctement
      await this.waitForCarouselRendered();
      
      // Marquer comme chargé
      this.loaded = true;
      this.cdf.detectChanges();
    } catch (error) {
      console.warn('Erreur lors de l\'initialisation du carousel:', error);
      // Continuer quand même si le carousel échoue
      this.loaded = true;
      this.cdf.detectChanges();
    }
  }

  private async waitForSlickElement(): Promise<void> {
    return new Promise<void>((resolve) => {
      const maxWait = 3000;
      const startTime = Date.now();

      const checkElement = () => {
        const elapsed = Date.now() - startTime;
        const carouselElement = document.querySelector('.splash-screen__carousel .slick-slider');
        
        if (carouselElement || elapsed > maxWait) {
          resolve();
        } else {
          setTimeout(checkElement, 50);
        }
      };

      checkElement();
    });
  }

  private async waitForSlickReady(): Promise<void> {
    return new Promise<void>((resolve) => {
      const maxWait = 5000;
      const startTime = Date.now();

      if (typeof $ === 'undefined') {
        // Si jQuery n'est pas disponible, attendre un délai
        setTimeout(() => resolve(), 1000);
        return;
      }

      const carouselElement = document.querySelector('.splash-screen__carousel .slick-slider');
      if (!carouselElement) {
        setTimeout(() => resolve(), 1000);
        return;
      }

      // Écouter l'événement init de Slick
      $(carouselElement).on('init', () => {
        resolve();
      });

      // Vérifier si le carousel est déjà initialisé
      const slickInstance = $(carouselElement).data('slick');
      if (slickInstance) {
        resolve();
        return;
      }

      // Timeout de sécurité
      setTimeout(() => {
        resolve();
      }, maxWait);
    });
  }

  private async waitForCarouselRendered(): Promise<void> {
    return new Promise<void>((resolve) => {
      const maxWait = 3000;
      const startTime = Date.now();

      const checkRendered = () => {
        const elapsed = Date.now() - startTime;
        const carouselElement = document.querySelector('.splash-screen__carousel .slick-slider') as HTMLElement;
        
        if (carouselElement) {
          const styles = window.getComputedStyle(carouselElement);
          const width = parseInt(styles.width || '0');
          const height = parseInt(styles.height || '0');
          const opacity = parseFloat(styles.opacity || '1');
          
          // Vérifier que le carousel est visible et a une taille
          const isRendered = width > 0 && height > 0 && opacity > 0;
          
          if (isRendered || elapsed > maxWait) {
            resolve();
          } else {
            requestAnimationFrame(checkRendered);
          }
        } else if (elapsed > maxWait) {
          resolve();
        } else {
          requestAnimationFrame(checkRendered);
        }
      };

      // Attendre un peu avant de commencer la vérification
      setTimeout(() => {
        requestAnimationFrame(checkRendered);
      }, 200);
    });
  }

  ngAfterViewChecked() {
    // Initialiser le carousel si pas encore fait (fallback)
    if (this.slickModal && !this.init) {
      this.initializeCarouselWithEvents();
    }
  }
}
